import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabaseClient';
import { AlertCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DeleteSoftwareConfirmationModal = ({ software, onClose, onConfirmDelete }) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setIsDeleting(true);
    setError('');
    try {
      const { error: deleteError } = await supabase
        .from('software_licenses')
        .delete()
        .eq('id', software.id);

      if (deleteError) {
        throw deleteError;
      }

      onConfirmDelete();
      onClose();
    } catch (err) {
      setError(err.message);
      console.error("Delete software error:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-md animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {t('software_licenses.confirm_deletion_title', 'Confirm Software Deletion')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isDeleting}>
            <X size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('software_licenses.confirm_deletion_message', { name: software?.name, defaultValue: `Are you sure you want to delete software license: ${software?.name}?` })}
          </p>
          
          <div className="p-4 bg-destructive/10 rounded-lg flex items-start gap-3">
            <AlertCircle className="text-destructive shrink-0" size={20} />
            <p className="text-sm text-destructive leading-relaxed">
              {t('software_licenses.confirm_deletion_warning', 'This action cannot be undone. All seat assignments for this software will be automatically revoked/deleted.')}
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-100 text-red-700 text-sm rounded-lg flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end p-6 border-t border-border gap-3">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            {t('software_licenses.cancel', 'Cancel')}
          </Button>
          <Button variant="destructive" loading={isDeleting} disabled={isDeleting} onClick={handleDelete}>
            {isDeleting ? t('software_licenses.deleting', 'Deleting...') : t('software_licenses.delete_software', 'Delete Software')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteSoftwareConfirmationModal;
